import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  makeStyles,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup"
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import apiHelper from "../../../util/ApiHelper";
import { toast } from "react-toastify";
import editorConfiguration from "../../../util/EditorConfiguration";
import { useLoading } from "../../../util/LoadingProvider";


const schema = yup.object().shape({
  aboutUs: yup.string(),
  localizations: yup.object().shape({
    en: yup.object().shape({
      content: yup.string(),
    }),
    tc: yup.object().shape({
      content: yup.string(),
    }),
  }),
});

const defaultFormValues = {
  title: "",
  sub_title: "",
  localizations: {
    en: {
      content: "",
    },
    tc: {
      content: "",
    },
  },
};

const useStyles = makeStyles((theme) => ({
  textInput: {
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "362px",
    },
  },
  form: {
    backgroundColor: "white",
    padding: "26px 20px",
  },
  buttonGroup: {
    marginTop: "40px",
    marginRight: "15px",
    textAlign: "right",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: 0,
  },
  input: {
    width: "100%",
  },
}));

export default function AboutUsPage({ disabled, ...props }) {
  const { match } = props;
  const id = match.params.id;
  const [defaultValues, setDefaultValues] = useState(defaultFormValues);
  const [resultValues, setResultValues] = React.useState(null);
  const { setLoading } = useLoading();

  const classes = useStyles();

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
    resolver: yupResolver(schema),
  });

  function newResultValues(key, value, lang){
    var newResultValues = resultValues;
    resultValues.forEach((resultValue, i) => {
      if(resultValue.optionKey == key){
        newResultValues[i].localizations[lang].optionValue = value;
      }
    });
    setResultValues(newResultValues);
  }

  function updateResultValues(data){
    for (const [dataKey, dataValue] of Object.entries(data)) {
      if(dataKey == "localizations"){
        for (const [localizationsKey, localizationsValue] of Object.entries(dataValue)) {
          for (const [key, value] of Object.entries(localizationsValue)) {
            newResultValues(key,value,localizationsKey)
          }
        }
      }else{
        newResultValues(dataKey,dataValue,"en")
      }
    };
  }

  const onSubmit = (value) => {
    updateResultValues(value);
    updateAboutUs(resultValues);
  };

  const getAboutUs = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiHelper.hkiffApp.getHkiffApp("about_us");
      console.log(result);
      const data = result.data;
      var newDefaultFormValues = defaultFormValues;
      var obj = data.reduce(function ( total, current ) {
        if(current.localizations.tc && newDefaultFormValues.localizations.tc[ current.optionKey ] != undefined){
          newDefaultFormValues["localizations"]["tc"][current.optionKey] = current.localizations.tc.optionValue;
          newDefaultFormValues["localizations"]["en"][current.optionKey] = current.localizations.en.optionValue;
        }else{
          newDefaultFormValues[ current.optionKey ] = current.localizations.en.optionValue;
        }
    }, {});
    console.log(newDefaultFormValues);
      setResultValues(result.data);
      setDefaultValues(newDefaultFormValues);
      reset(newDefaultFormValues);
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  }, [id, setLoading]);

  const updateAboutUs = async (value) => {
    try {
      const result = await apiHelper.hkiffApp.updateHkiffApp("about_us", value);
      console.log(result);
      if (result.status === 200) {
        toast.dark("Your changes are saved.");
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getAboutUs();
  }, [getAboutUs]);

  return (
    <div className={classes.content}>
      <Box width="100%">
        <Typography variant="h4" gutterBottom>
          About Us
        </Typography>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
        <Grid container xs={12} direction="row" spacing={3}>
          <Grid item xs={12}>
            <Controller
              name="title"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label="Page Title"
                  variant="outlined"
                  margin="dense"
                  className={classes.textInput}
                  fullWidth
                  {...field}
                  error={!!error}
                  helperText={error ? error.message : ""}
                />
              )}
            />
          </Grid>
        </Grid>

        {defaultValues && (
          <>
            <Box
              fontWeight={900}
              fontSize={13}
              mb={4}
              style={{ marginTop: "30px" }}
              width="100%"
            >
              <Typography variant="p">Content (English)</Typography>
            </Box>

            <Grid container xs={12} direction="row" spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="localizations.en.content"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <CKEditor
                      editor={Editor}
                      config={editorConfiguration}
                      data={field.value}
                      disabled={disabled}
                      onReady={(editor) => {
                        // You can store the "editor" and use when it is needed.
                        console.log("Editor is ready to use!", editor);
                        editor.setData(defaultValues.localizations.en.content);
                      }}
                      onChange={(event, editor) => {
                        const data = editor.getData();
                        field.onChange(data);
                        // console.log({ event, editor, data });
                      }}
                      onBlur={(event, editor) => {
                        console.log("Blur.", editor);
                      }}
                      onFocus={(event, editor) => {
                        console.log("Focus.", editor);
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Box fontWeight={900} fontSize={13} mt={4} mb={4} width="100%">
              <Typography variant="p">Content (Traditional Chinese)</Typography>
            </Box>

            <Grid container xs={12} direction="row" spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="localizations.tc.content"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <CKEditor
                      editor={Editor}
                      config={editorConfiguration}
                      data={field.value}
                      disabled={disabled}
                      onReady={(editor) => {
                        // You can store the "editor" and use when it is needed.
                        console.log("Editor is ready to use!", editor);
                        editor.setData(defaultValues.localizations.tc.content);
                      }}
                      onChange={(event, editor) => {
                        const data = editor.getData();
                        field.onChange(data);
                        // console.log({ event, editor, data });
                      }}
                      onBlur={(event, editor) => {
                        console.log("Blur.", editor);
                      }}
                      onFocus={(event, editor) => {
                        console.log("Focus.", editor);
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </>
        )}
        


        {disabled || (
          <div className={classes.buttonGroup}>
            <Button
              type="button"
              style={{ marginRight: "20px" }}
            >
              Preview
            </Button>
            <Button
              type="submit"
              variant="contained"
              disableElevation
              style={{
                color: "white",
                background: "#306F9D",
                marginRight: "5px",
                padding: "9px 16px",
              }}
            >
              Save
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
